import { ErrorHandler, Injectable } from '@angular/core';

// When a deploy lands and a user's currently-loaded `main.js` references a
// lazy chunk filename that no longer exists in the new build (S3 sync runs
// with `--delete`), the next router-triggered import fails with a
// ChunkLoadError. Without a handler the user lands on a broken/white page.
// Forcing a reload here is a strong recovery signal: the user was actively
// trying to navigate, so a refresh costs them nothing and gets them onto
// the latest bundle immediately.

const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [/Loading chunk \S+ failed/i, /Loading CSS chunk \S+ failed/i, /ChunkLoadError/i];

const RELOAD_GUARD_KEY = 'fina:chunk-reload-attempted-at';

// Don't reload more than once per minute even if multiple chunk errors fire
// in quick succession — protects against an infinite reload loop if for any
// reason the new bundle ALSO can't load the chunk (e.g. CloudFront edge in
// the middle of an invalidation propagation, S3 still consistent).
const MIN_MS_BETWEEN_RELOADS = 60 * 1000;

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    handleError(error: unknown): void {
        if (this.isChunkLoadError(error)) {
            if (this.canAttemptReload()) {
                this.markReloadAttempt();
                console.warn('[GlobalErrorHandler] ChunkLoadError detected — reloading to pick up latest bundle.', error);
                globalThis.location.reload();
                return;
            }
            console.error('[GlobalErrorHandler] ChunkLoadError but reload was just attempted — surfacing error to user.', error);
        }

        console.error(error);
    }

    private isChunkLoadError(error: unknown): boolean {
        if (!error) return false;
        const err = error as { name?: string; message?: string; toString?: () => string };
        const haystack = `${err.name || ''} ${err.message || ''} ${typeof err.toString === 'function' ? err.toString() : ''}`;
        return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(haystack));
    }

    private canAttemptReload(): boolean {
        try {
            const previousAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
            return Date.now() - previousAttempt > MIN_MS_BETWEEN_RELOADS;
        } catch {
            // sessionStorage can throw in some privacy modes — fall back to allowing the reload.
            return true;
        }
    }

    private markReloadAttempt(): void {
        try {
            sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        } catch {
            // Ignore — sessionStorage unavailable.
        }
    }
}
